import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Typography } from 'seagull';
import { billingStagesTextsPath } from '~/business-logic/billingLogic';
import BillingTriggerDropdown from '~/components/ServiceBuilder/BillingTriggerDropdown';
import OccurrenceTextField from '~/components/ServiceBuilder/OccurrenceTextField/OccurrenceTextField';
import PreapprovedTextField from '~/components/ServiceBuilder/PreapprovedTextField';
import PricingTextField from '~/components/ServiceBuilder/PricingTextField';
import { defaultInitialState } from '~/components/ServiceBuilder/ServiceBuilder';
import { serviceToInput } from '~/components/ServiceBuilder/ServiceBuilder.utils';
import UnitNameTextField from '~/components/ServiceBuilder/UnitNameTextField';
import { BillingSettingsSection } from '~/components/ServiceDetails/BillingSettingsSection/BillingSettingsSection';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { autoBillingPeriodLabel } from '~/utils/service-billing/automatic-service-billing-period';
import EditableTableCell from '../EditableTableCell/EditableTableCell';
import { BillingCellSubtitle } from './BillingCellSubtitle/BillingCellSubtitle';
import { billingFormToService } from './service.utils';
import styles from './ServiceBillingCell.module.scss';
import { ServiceBillingModalContent } from './ServiceBillingModalContent/ServiceBillingModalContent';
const ServiceBillingContent = (param)=>{
    let { draft, service, setDraft } = param;
    const { watch, getValues, setError, clearErrors } = useFormContext();
    const [pricing] = watch([
        'pricing'
    ]);
    if (!service) return null;
    watch((value)=>{
        const serviceNewData = _object_spread({}, getValues(), value);
        try {
            setDraft(draft.setService(service.id, (s)=>billingFormToService({
                    service: s,
                    billingForm: serviceNewData
                })));
        } catch (err) {}
    });
    const onDayOfMonthChange = (serviceId, dayOfMonth)=>{
        setDraft(draft.setService(service.id, (s)=>serviceModel.setBillingDayOfMonth(s, dayOfMonth)));
    };
    const onDayOfWeekChange = (serviceId, dayOfWeek)=>{
        setDraft(draft.setService(service.id, (s)=>serviceModel.setBillingDayOfWeek(s, dayOfWeek)));
    };
    const onBillUpfrontModeChange = (serviceId, billUpfront)=>{
        setDraft(draft.setService(service.id, (s)=>serviceModel.setBillUpfront(s, billUpfront)));
    };
    const onDayOfYearChange = (serviceId, dayOfYear)=>{
        setDraft(draft.setService(service.id, (s)=>serviceModel.setBillingDayOfYear(s, dayOfYear)));
    };
    const onHasError = (serviceId, hasError)=>{
        if (hasError) {
            setError('billingDayOfMonth', {
                message: ''
            });
        } else {
            clearErrors('billingDayOfMonth');
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.serviceBillingCell,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.row,
                children: [
                    /*#__PURE__*/ _jsx(PricingTextField, {}),
                    /*#__PURE__*/ _jsx(OccurrenceTextField, {
                        serviceOrigin: "draft",
                        service: service
                    })
                ]
            }),
            [
                'hour',
                'unit'
            ].includes(pricing) && /*#__PURE__*/ _jsxs("div", {
                className: styles.row,
                children: [
                    pricing === 'unit' && /*#__PURE__*/ _jsx(UnitNameTextField, {
                        testId: "sb-unit-name"
                    }),
                    /*#__PURE__*/ _jsx(PreapprovedTextField, {})
                ]
            }),
            /*#__PURE__*/ _jsx(BillingTriggerDropdown, {
                service: service,
                serviceOrigin: "draft",
                effectiveDate: draft.effectiveDate(),
                stagesClassName: styles.stages
            }),
            /*#__PURE__*/ _jsx(BillingSettingsSection, {
                service: service,
                testId: "edit-service-billing-modal",
                isActiveAgreement: false,
                onDayOfMonthChange: onDayOfMonthChange,
                onDayOfWeekChange: onDayOfWeekChange,
                onDayOfYearChange: onDayOfYearChange,
                onBillUpfrontModeChange: onBillUpfrontModeChange,
                onHasError: onHasError
            })
        ]
    });
};
export default function ServiceBillingCell(param) {
    let { index, service, isDisabled, disabledTooltip, onOpenModal } = param;
    const { START_BILLING_AT_PHASE_2_INLINE } = useFeatureFlags();
    const { t: billingStagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const defaultValues = useMemo(()=>service ? serviceToInput({
            service,
            qboItems: [],
            xeroItems: []
        }) : defaultInitialState, [
        service
    ]);
    const form = useForm({
        mode: 'all',
        defaultValues
    });
    const onClick = ()=>{
        form.reset(defaultValues);
        onOpenModal({
            width: '640',
            renderContent: (param)=>{
                let { draft, setDraft } = param;
                return {
                    title: 'Service billing',
                    testId: "edit-service-billing-modal-".concat(index),
                    onSubmit: (defaultBehavior)=>{
                        form.handleSubmit(defaultBehavior)();
                    },
                    content: START_BILLING_AT_PHASE_2_INLINE ? /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                        children: /*#__PURE__*/ _jsx(ServiceBillingModalContent, {
                            service: draft.service(service.id),
                            draft: draft,
                            setDraft: setDraft,
                            testId: "edit-service-billing-modal-".concat(index)
                        })
                    })) : /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                        children: /*#__PURE__*/ _jsx(ServiceBillingContent, {
                            service: draft.service(service.id),
                            draft: draft,
                            setDraft: setDraft
                        })
                    }))
                };
            }
        });
    };
    const subtitle = useMemo(()=>{
        if (serviceBillingModel.isAutomatic(service.billing)) {
            const label = autoBillingPeriodLabel(service.billing);
            return label ? label.text : undefined;
        }
        return serviceCostModel.stringifyServiceCost(service.cost).unitCapDisplay;
    }, [
        service.billing,
        service.cost
    ]);
    return /*#__PURE__*/ _jsxs(EditableTableCell, {
        testId: "edit-service-billing-cell-".concat(index),
        onClick: onClick,
        isDisabled: isDisabled,
        disabledTooltip: disabledTooltip,
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                testId: "service-billing-".concat(index),
                children: [
                    serviceModel.formatRecurrencePeriod(service),
                    ' • ',
                    serviceModel.formatBillingTrigger(service, billingStagesTexts)
                ]
            }),
            START_BILLING_AT_PHASE_2_INLINE ? /*#__PURE__*/ _jsx(BillingCellSubtitle, {
                service: service,
                testId: "service-billing-subtitle-".concat(index)
            }) : /*#__PURE__*/ _jsx(Typography, {
                testId: "service-billing-subtitle-".concat(index),
                variant: "paragraph2",
                skin: "secondary",
                children: subtitle
            })
        ]
    });
}
